/**
 * Theme management with three layers, in order of precedence at first
 * paint:
 *
 *  1. The inline bootstrap script in `index.html` reads `localStorage`
 *     and toggles the `dark` / `light` class on `<html>` BEFORE React
 *     mounts. This is what kills the FOUC on every reload.
 *
 *  2. <ThemeProvider> mirrors the current class state into a React
 *     context so any component can read or change it.
 *
 *  3. After auth resolves, the provider asks `getUiPrefs` for the
 *     server copy. If it differs from local, the server wins (this is
 *     how a theme set on phone A appears on laptop B). Each setTheme()
 *     call writes both: localStorage for instant, Firestore for sync.
 */
import * as React from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { useUser } from '../auth/AuthProvider';

export type Theme = 'light' | 'dark';

interface ThemeContextValue {
  theme: Theme;
  setTheme: (next: Theme) => void;
  toggleTheme: () => void;
}

const STORAGE_KEY = 'cp3-theme';

const ThemeContext = React.createContext<ThemeContextValue>({
  theme: 'dark',
  setTheme: () => {},
  toggleTheme: () => {},
});

function readInitialTheme(): Theme {
  if (typeof document === 'undefined') return 'dark';
  return document.documentElement.classList.contains('light') ? 'light' : 'dark';
}

function applyTheme(next: Theme): void {
  const root = document.documentElement;
  if (next === 'light') {
    root.classList.add('light');
    root.classList.remove('dark');
  } else {
    root.classList.add('dark');
    root.classList.remove('light');
  }
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    /* localStorage blocked — ignore */
  }
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = React.useState<Theme>(readInitialTheme);
  const { user } = useUser();

  const setTheme = React.useCallback((next: Theme) => {
    applyTheme(next);
    setThemeState(next);
    // Best-effort cross-device sync — fire-and-forget so the UI never
    // waits for the network on a click.
    void persistRemote(next);
  }, []);

  const toggleTheme = React.useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  }, [theme, setTheme]);

  // After auth resolves, fetch the server copy. If the server has a
  // different theme than what we bootstrapped from localStorage, prefer
  // the server (cross-device "follow me" semantics).
  React.useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const fn = httpsCallable<unknown, { prefs: { theme: Theme } }>(
          functions,
          'getUiPrefs',
        );
        const r = await fn({});
        const remote = r.data.prefs?.theme;
        if (cancelled || !remote) return;
        if (remote !== theme) {
          applyTheme(remote);
          setThemeState(remote);
        }
      } catch {
        /* offline or first-time user — keep the local choice */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const value = React.useMemo<ThemeContextValue>(
    () => ({ theme, setTheme, toggleTheme }),
    [theme, setTheme, toggleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

async function persistRemote(theme: Theme): Promise<void> {
  try {
    const fn = httpsCallable<{ theme: Theme }, { ok: true }>(functions, 'setUiPrefs');
    await fn({ theme });
  } catch {
    /* not signed in or offline — local copy is authoritative */
  }
}

export function useTheme(): ThemeContextValue {
  return React.useContext(ThemeContext);
}
